/**
 * Global axios interceptors — attached to the DEFAULT axios instance so they
 * apply to every page that does `import axios from 'axios'` (56 of them at
 * last count). Loaded once from App.js entry.
 *
 * Behaviour:
 *   1. Adds a 30-second default timeout to any request that didn't set one
 *      (prevents Cloudflare-side hangs from freezing the UI indefinitely).
 *   2. Auto-retries IDEMPOTENT requests (GET / HEAD / OPTIONS) up to TWICE
 *      with progressive backoff (800ms, 2500ms) on transient errors:
 *        - No response (network blip, CORS-aborted preflight)
 *        - ECONNABORTED (request timeout)
 *        - ERR_NETWORK (browser couldn't establish connection)
 *        - HTTP 502 / 503 / 504 (platform / single-worker saturation)
 *      Total grace window: ~17s before the user actually sees an error.
 *   3. NEVER retries POST / PUT / PATCH / DELETE — these are non-idempotent
 *      and could double-charge (Paystack), duplicate SOAP notes, etc.
 *   4. Adds a friendly toast for transient errors once we've exhausted
 *      retries, so users see "PlayFirst is catching up" instead of a stack
 *      trace. Toast is throttled — at most one every 4 seconds.
 *
 * This is the single biggest UX resilience win we can make WITHOUT touching
 * 56 page files individually.
 */
import axios from 'axios';
import { toast } from 'sonner';

const RETRIABLE_METHODS = new Set(['get', 'head', 'options']);
const RETRY_BACKOFFS_MS = [800, 2500]; // up to 2 retries

let lastToastAt = 0;
const TOAST_THROTTLE_MS = 4000;

const shouldRetry = (error) => {
  const status = error.response?.status;
  const code = error.code;
  // No response at all (network down, CORS-aborted, browser canceled)
  if (!error.response) return true;
  // Transient platform / saturation responses
  if (status === 502 || status === 503 || status === 504) return true;
  // Axios-side timeout / network error codes
  if (code === 'ECONNABORTED' || code === 'ERR_NETWORK') return true;
  return false;
};

const friendlyToast = () => {
  const now = Date.now();
  if (now - lastToastAt < TOAST_THROTTLE_MS) return;
  lastToastAt = now;
  toast.error('PlayFirst is catching up — please try again in a moment.', {
    duration: 4000,
  });
};

// 1. Default timeout if the caller didn't set one.
axios.interceptors.request.use((config) => {
  if (!config.timeout) {
    config.timeout = 30000;
  }
  return config;
});

// 2. Auto-retry interceptor.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config) {
      return Promise.reject(error);
    }
    const method = (config.method || 'get').toLowerCase();
    config.__retryCount = config.__retryCount || 0;

    const isIdempotent = RETRIABLE_METHODS.has(method);
    const canRetry =
      isIdempotent &&
      shouldRetry(error) &&
      config.__retryCount < RETRY_BACKOFFS_MS.length &&
      config.__retry !== false;

    if (canRetry) {
      const delay = RETRY_BACKOFFS_MS[config.__retryCount];
      config.__retryCount += 1;
      // Each retry also gets a longer timeout so a slow cold-start can recover.
      config.timeout = (config.timeout || 30000) + 5000 * config.__retryCount;
      await new Promise((r) => setTimeout(r, delay));
      try {
        return await axios(config);
      } catch (retryErr) {
        // Fall through to the error path — the retry counter persists on
        // the same `config` object so subsequent retries continue.
        return Promise.reject(retryErr);
      }
    }

    // Show a single friendly toast for transient errors that exhausted retries.
    // Auth / form-error responses (401, 400, 422) bypass the toast.
    if (shouldRetry(error) && isIdempotent && !error.config?.__suppressToast) {
      friendlyToast();
    }

    return Promise.reject(error);
  }
);

export {};
